/**
 * The brain: inputs flowing in, outputs flowing out, sized and counted from real data.
 *
 * HISTORY, because it matters for what this is allowed to be. His mock centres a large
 * decorative brain visualisation. I dropped it twice, arguing it was the best-looking
 * thing on the page and carried no information. He asked for it a third time, plainly:
 * "Brain, Live nodes, Animation". So it stays, and the objection is answered a
 * different way: every spoke carries a REAL count, the core pulses at a rate set by how
 * much actually flowed, and clicking a spoke goes somewhere. It is a diagram of his
 * system, not an ornament of one.
 *
 * Implementation constraints that are not negotiable here:
 *  - Inline SVG, no external assets. This is an offline-first PWA under a strict CSP.
 *  - `currentColor` and token-driven fills, so it follows the light/dark toggle.
 *  - Animation on `opacity` and `transform` only, and stopped under reduced motion.
 *  - Nothing is fabricated: a zero count renders as zero and its spoke dims.
 */

import { Icon, type IconName } from './Icon.js';

export interface BrainSpoke {
  label: string;
  count: number;
  icon: IconName;
  /** What clicking it should search for. Empty means it is not clickable. */
  target: string;
}

export interface BrainProps {
  inputs: BrainSpoke[];
  outputs: BrainSpoke[];
  /** Total notes, shown in the core. */
  total: number;
  onPick: (target: string) => void;
}

/** Evenly spaced vertical positions, so a spoke list of any length stays balanced. */
const spread = (i: number, n: number): number => (n === 1 ? 50 : 12 + (i * 76) / (n - 1));

/**
 * One network edge per spoke, and each node sits at the HEIGHT OF ITS OWN LABEL.
 *
 * The first attempt placed them on two 150 degree arcs. The maths was right and the result was
 * wrong: with four nodes spread over 150 degrees, the arc endpoints land near the poles, so the
 * ring read as clustered at top and bottom rather than as inputs left and outputs right. Worse,
 * no node was anywhere near the label it belonged to.
 *
 * Lining each node up with its spoke row fixes both. `spread` is the same function that positions
 * the rows, so the node is literally beside its own text and the panel reads as a hub wired to the
 * things either side of it.
 *
 * Node radius scales with the LOG of the count: his mail lane is in the hundreds while objectives
 * are in single figures, and a linear scale would render the small lanes as invisible specks.
 */
function buildEdges(inputs: BrainSpoke[], outputs: BrainSpoke[]) {
  const rows = [
    ...inputs.map((s, i) => ({ s, i, n: inputs.length, dir: 'in' as const })),
    ...outputs.map((s, i) => ({ s, i, n: outputs.length, dir: 'out' as const })),
  ];
  return rows.map(({ s, i, n, dir }, k) => {
    // viewBox is 0..200 and `spread` returns a percentage, so it doubles into user units.
    const y = spread(i, n) * 2;
    const x = dir === 'in' ? 16 : 184;
    // Where the edge leaves the emblem, clamped so it starts outside the hexagon rather than
    // inside the readout.
    const fromX = dir === 'in' ? 46 : 154;
    const fromY = 100 + (y - 100) * 0.34;
    return {
      key: `${dir}-${s.label}`,
      x,
      y,
      fromX,
      fromY,
      dir,
      quiet: s.count === 0,
      r: 2.2 + Math.min(3.2, Math.log10(Math.max(1, s.count)) * 1.4),
      // Varied from the index rather than randomly, so the network breathes without a re-render
      // reshuffling every animation.
      delay: (k % 5) * 0.7,
      speed: 3.6 + (k % 3) * 0.8,
    };
  });
}

export function Brain({ inputs, outputs, total, onPick }: BrainProps): JSX.Element {
  const edges = buildEdges(inputs, outputs);
  return (
    <section className="brain" aria-label="What flows through the brain">
      <ul className="brain-side brain-in">
        {inputs.map((s, i) => (
          <SpokeRow key={s.label} spoke={s} side="in" i={i} n={inputs.length} onPick={onPick} />
        ))}
      </ul>

      <div className="brain-core">
        {/*
          A LIVING NETWORK, not a picture of a sphere.
          
          The previous core was a raster hologram plate, and it had two problems he spotted at
          once: its own near-black did not match the panel, so a faint rectangle showed around it,
          and its projection haze was clipped square by the panel edge. Both are gone because
          nothing here is an image. Everything below is inline SVG, which is also what the CSP and
          the offline-first requirement want.
          
          THE MOTION IS A DIAGRAM, which is the part worth protecting. One node per spoke, sized by
          its real count, on the side it belongs to. Signals travel INWARD along input edges and
          OUTWARD along output edges, so the direction of movement is the direction of flow through
          his system. A quiet lane's node dims and its signal stops, exactly as its spoke does.
          Random drifting dots would have looked similar and meant nothing.
        */}
        <svg className="brain-net" viewBox="0 0 200 200" aria-hidden="true">
          <defs>
            <radialGradient id="brain-glow" cx="50%" cy="50%" r="50%">
              <stop offset="0%" stopColor="var(--accent)" stopOpacity="0.22" />
              <stop offset="65%" stopColor="var(--accent)" stopOpacity="0.05" />
              <stop offset="100%" stopColor="var(--accent)" stopOpacity="0" />
            </radialGradient>
          </defs>

          <circle cx="100" cy="100" r="96" fill="url(#brain-glow)" />

          {/* Emanation: rings leaving the core. Scale and opacity only, so no layout is touched. */}
          <circle className="brain-pulse brain-pulse-1" cx="100" cy="100" r="34" />
          <circle className="brain-pulse brain-pulse-2" cx="100" cy="100" r="34" />
          <circle className="brain-pulse brain-pulse-3" cx="100" cy="100" r="34" />

          {/*
            The MO emblem, ENCLOSING the readout rather than hiding behind it.
            
            Drawn at the centre first, it was invisible: the node count sits exactly there and
            covered it completely. So the hexagon is scaled up to frame the number, its two arcs sit
            under it, and the MO monogram itself drops to the foot of the badge where nothing
            competes. The count stays the brightest thing in the panel, which is correct, and the
            emblem is now actually legible as an emblem.
          */}
          <g className="brain-mark">
            <polygon points="100,42 150,71 150,129 100,158 50,129 50,71" />
            <path className="brain-mark-arc" d="M64 132 a40 40 0 0 0 72 0" />
            <path className="brain-mark-arc" d="M72 138 a30 30 0 0 0 56 0" />
          </g>
          {/* The monogram, small and stencil-like, at the foot of the hexagon. */}
          <g className="brain-mark brain-mark-mo">
            <path d="M86 150 v-11 l5 6 5-6 v11" />
            <circle cx="108" cy="145" r="5.5" />
          </g>

          {edges.map((e) => (
            <line
              key={`edge-${e.key}`}
              className="brain-link"
              data-quiet={e.quiet}
              x1={e.fromX}
              y1={e.fromY}
              x2={e.x}
              y2={e.y}
            />
          ))}

          {/* The travelling signal: one short dash per edge, offset-animated along it. */}
          {edges.map((e) => (
            <line
              key={`sig-${e.key}`}
              className="brain-signal"
              data-dir={e.dir}
              data-quiet={e.quiet}
              x1={e.fromX}
              y1={e.fromY}
              x2={e.x}
              y2={e.y}
              style={{ animationDelay: `${e.delay}s`, animationDuration: `${e.speed}s` }}
            />
          ))}

          {edges.map((e) => (
            <circle
              key={`node-${e.key}`}
              className="brain-node"
              data-quiet={e.quiet}
              cx={e.x}
              cy={e.y}
              r={e.r}
              style={{ animationDelay: `${e.delay}s` }}
            />
          ))}
        </svg>
        <div className="brain-readout">
          <span className="brain-total">{total.toLocaleString()}</span>
          <span className="brain-total-label">nodes</span>
        </div>
      </div>

      <ul className="brain-side brain-out">
        {outputs.map((s, i) => (
          <SpokeRow key={s.label} spoke={s} side="out" i={i} n={outputs.length} onPick={onPick} />
        ))}
      </ul>
    </section>
  );
}

function SpokeRow({
  spoke,
  side,
  i,
  n,
  onPick,
}: {
  spoke: BrainSpoke;
  side: 'in' | 'out';
  i: number;
  n: number;
  onPick: (target: string) => void;
}): JSX.Element {
  // A spoke with nothing behind it dims rather than disappearing: the shape of the
  // system should stay legible even when one lane is quiet.
  const quiet = spoke.count === 0;
  return (
    <li
      className="brain-spoke-row"
      data-side={side}
      data-quiet={quiet}
      style={{ top: `${spread(i, n)}%` }}
    >
      <button
        type="button"
        className="brain-spoke-btn"
        disabled={spoke.target === ''}
        onClick={() => onPick(spoke.target)}
        title={spoke.target === '' ? `${spoke.label}: nothing to open` : `Open ${spoke.label}`}
      >
        <Icon name={spoke.icon} size={15} />
        <span className="brain-spoke-label">{spoke.label}</span>
        <span className="brain-spoke-count">{spoke.count}</span>
      </button>
    </li>
  );
}
